import { useEffect, useState } from 'react'
import { api, get } from '../lib/api'
import { tw } from './strings'

interface Standing {
  handle: string
  orphaned: { id: string; name: string; liveJobs: number }[]
}

/**
 * Deleting your account.
 *
 * Apple requires this to exist inside the app for anything that creates
 * accounts, and viequesworx.com/delete-account/ covers the person who has
 * already uninstalled it. Both say the same thing, which took a correction:
 * the website described this screen a day before it existed.
 *
 * ── Two deliberate frictions, and nothing beyond them ──────────────────────
 *
 * It is behind a disclosure, and it asks for the username to be typed. That is
 * the whole of it. Anything more — a cooling-off period, an email loop, a
 * "tell us why" — is a dark pattern wearing a safety jacket, and this is a
 * right rather than a request.
 *
 * The button does not say "Continue". It says what it does.
 */
export default function DeleteAccount({ onDeleted }: { onDeleted: () => void | Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [standing, setStanding] = useState<Standing | null>(null)
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Loaded only when the section is opened: nobody needs this query on every
  // visit to Profile.
  useEffect(() => {
    if (!open || standing) return
    void get<Standing>('/api/me/deletion').then(setStanding).catch(() => {})
  }, [open, standing])

  async function destroy() {
    setBusy(true)
    setError(null)
    try {
      await api('/api/account', { method: 'DELETE', body: JSON.stringify({ confirm }) })
      await onDeleted()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.work'))
      setBusy(false)
    }
  }

  const ready = standing !== null && confirm.trim().toLowerCase() === standing.handle

  return (
    <section className="w-danger">
      <button className="link w-danger-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {tw('w.delete.open')}
      </button>

      {open && (
        <div className="w-danger-body">
          <h3>{tw('w.delete.title')}</h3>
          <p className="muted">{tw('w.delete.what')}</p>

          {/*
            The businesses this would strand, BY NAME.
            "You may be the only owner of a business" is a sentence somebody
            reads past; "You are the only member of La Palma Café, which has 2
            live postings" is one they stop at.
          */}
          {Boolean(standing?.orphaned.length) && (
            <div className="warn w-banner">
              <p>{tw('w.delete.orphanWarning')}</p>
              <ul>
                {standing!.orphaned.map((o) => (
                  <li key={o.id}>
                    <strong>{o.name}</strong>
                    {o.liveJobs > 0 && ` — ${o.liveJobs} ${tw('w.delete.livePostings')}`}
                  </li>
                ))}
              </ul>
              <p>{tw('w.delete.orphanOutcome')}</p>
            </div>
          )}

          <label>
            <span className="w-label">{tw('w.delete.confirmLabel')}</span>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={standing?.handle ?? ''}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          </label>

          {error && <p className="error">{error}</p>}

          <button className="w-destroy" disabled={!ready || busy} onClick={() => void destroy()}>
            {busy ? '…' : tw('w.delete.confirm')}
          </button>
          <p className="muted small">{tw('w.delete.permanent')}</p>
        </div>
      )}
    </section>
  )
}
